/**
 * APX WorkCenter — Xero Financial Reports
 * Pulls live Balance Sheet, P&L, Trial Balance, and Executive Summary reports
 * directly from the Xero API. Accessible to Admin and Controller roles.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useEffectiveRole } from "@/contexts/PreviewContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReportRow {
  RowType: "Header" | "Section" | "Row" | "SummaryRow";
  Title?: string;
  Cells?: Array<{ Value: string; Attributes?: Array<{ Value: string; Id: string }> }>;
  Rows?: ReportRow[];
}

interface XeroReport {
  ReportID: string;
  ReportName: string;
  ReportType: string;
  ReportTitles: string[];
  ReportDate: string;
  UpdatedDateUTC: string;
  Rows: ReportRow[];
}

type ReportTab = "balance-sheet" | "profit-loss" | "trial-balance" | "cash-summary" | "executive-summary";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfYear(): string {
  return `${new Date().getFullYear()}-01-01`;
}

/** Returns the last day of the prior month as YYYY-MM-DD (e.g. "2026-03-31" when current month is April 2026) */
function priorMonthEnd(): string {
  const now = new Date();
  // Day 0 of the current month = last day of the prior month
  const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
  return lastDay.toISOString().slice(0, 10);
}

function formatCurrency(val: string): string {
  const trimmed = val.trim();
  // Handle Xero parenthetical negatives: (2000000.00) → -2000000.00
  const isParenNeg = /^\([\d,]+(\.[\d]+)?\)$/.test(trimmed);
  const cleaned = isParenNeg ? "-" + trimmed.slice(1, -1).replace(/,/g, "") : trimmed.replace(/,/g, "");
  const n = parseFloat(cleaned);
  if (isNaN(n)) return val;
  const formatted = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${formatted})` : formatted;
}

function isCurrencyCell(val: string): boolean {
  const trimmed = val.trim();
  // Match plain numeric: optional negative, digits/commas, optional decimal part
  if (/^-?[\d,]+(\.\d+)?$/.test(trimmed)) return true;
  // Match Xero parenthetical negatives: (2000000.00)
  if (/^\([\d,]+(\.\d+)?\)$/.test(trimmed)) return true;
  return false;
}

/**
 * Converts Xero's short date header format (e.g. "4 APR 26") to a readable form ("Apr 4, 2026").
 * Leaves non-date strings (e.g. "Description", "Amount") unchanged.
 */
function formatXeroHeaderDate(val: string): string {
  // Match pattern: "D MMM YY" or "D MMM YYYY" (case-insensitive)
  const match = val.trim().match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{2,4})$/);
  if (!match) return val;
  const [, day, mon, yr] = match;
  const year = yr.length === 2 ? `20${yr}` : yr;
  const monthMap: Record<string, string> = {
    JAN: "Jan", FEB: "Feb", MAR: "Mar", APR: "Apr", MAY: "May", JUN: "Jun",
    JUL: "Jul", AUG: "Aug", SEP: "Sep", OCT: "Oct", NOV: "Nov", DEC: "Dec",
  };
  const monthStr = monthMap[mon.toUpperCase()] ?? mon;
  return `${monthStr} ${parseInt(day, 10)}, ${year}`;
}

// ─── Report Table Renderer ────────────────────────────────────────────────────

function ReportTable({ report }: { report: XeroReport }) {
  if (!report?.Rows?.length) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "oklch(0.14 0.04 240)" }}>
        No data available for this report.
      </div>
    );
  }

  const rows: React.ReactNode[] = [];

  function renderRow(row: ReportRow, depth = 0): void {
    if (row.RowType === "Header") {
      if (row.Cells?.length) {
        rows.push(
          <tr key={`header-${rows.length}`} style={{ background: "oklch(0.93 0.008 240)" }}>
            {row.Cells.map((cell, i) => (
              <th
                key={i}
                style={{
                  padding: "10px 14px",
                  textAlign: i === 0 ? "left" : "right",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "oklch(0.08 0.01 240)",
                  letterSpacing: "0.03em",
                  borderBottom: "1px solid oklch(0.92 0.008 240)",
                }}
              >
                {formatXeroHeaderDate(cell.Value)}
              </th>
            ))}
          </tr>
        );
      }
    } else if (row.RowType === "Section") {
      if (row.Title) {
        rows.push(
          <tr key={`section-${rows.length}`}>
            <td
              colSpan={10}
              style={{
                padding: "10px 14px",
                paddingTop: depth === 0 ? "18px" : "10px",
                fontSize: "13px",
                fontWeight: 700,
                color: "oklch(0.14 0.04 240)",
                background: "oklch(0.95 0.006 240)",
                borderTop: "1px solid oklch(0.97 0.005 85)",
                letterSpacing: "0.02em",
              }}
            >
              {row.Title}
            </td>
          </tr>
        );
      }
      if (row.Rows?.length) {
        row.Rows.forEach((r) => renderRow(r, depth + 1));
      }
    } else if (row.RowType === "Row") {
      // Skip "Total Comprehensive Income" row — not shown on APX reports
      if (row.Cells?.[0]?.Value?.trim() === "Total Comprehensive Income") return;
      if (row.Cells?.length) {
        rows.push(
          <tr
            key={`row-${rows.length}`}
            style={{
              borderBottom: "1px solid oklch(0.88 0.01 240)",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLTableRowElement).style.background = "oklch(0.18 0.04 240 / 15%)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLTableRowElement).style.background = "";
            }}
          >
            {row.Cells.map((cell, i) => {
              const isCurr = i > 0 && isCurrencyCell(cell.Value);
              const val = isCurr ? formatCurrency(cell.Value) : cell.Value;
              const isNeg = isCurr && parseFloat(cell.Value) < 0;
              return (
                <td
                  key={i}
                  data-neg={isNeg ? "true" : undefined}
                  style={{
                    padding: "8px 14px",
                    paddingLeft: i === 0 ? `${14 + depth * 16}px` : "14px",
                    fontSize: "13px",
                    textAlign: i === 0 ? "left" : "right",
                    color: isNeg
                      ? "oklch(0.45 0.15 25)"
                      : isCurr
                      ? "oklch(0.18 0.04 240)"
                      : "oklch(0.08 0.01 240)",
                    fontVariantNumeric: "tabular-nums",
                    fontFamily: isCurr ? "monospace, sans-serif" : "inherit",
                  }}
                >
                  {val}
                </td>
              );
            })}
          </tr>
        );
      }
    } else if (row.RowType === "SummaryRow") {
      // Skip "Total Comprehensive Income" summary row
      if (row.Cells?.[0]?.Value?.trim() === "Total Comprehensive Income") return;
      if (row.Cells?.length) {
        rows.push(
          <tr
            key={`summary-${rows.length}`}
            data-summary="true"
            style={{
              background: "oklch(0.90 0.008 240)",
              borderTop: "2px solid oklch(0.78 0.02 240)",
            }}
          >
            {row.Cells.map((cell, i) => {
              const isCurr = i > 0 && isCurrencyCell(cell.Value);
              const val = isCurr ? formatCurrency(cell.Value) : cell.Value;
              const isNeg = isCurr && parseFloat(cell.Value) < 0;
              return (
                <td
                  key={i}
                  style={{
                    padding: "10px 14px",
                    fontSize: "13px",
                    fontWeight: 700,
                    textAlign: i === 0 ? "left" : "right",
                    color: isNeg
                      ? "oklch(0.45 0.15 25)"
                      : isCurr
                      ? "oklch(0.18 0.04 240)"
                      : "oklch(0.08 0.01 240)",
                    fontVariantNumeric: "tabular-nums",
                    fontFamily: isCurr ? "monospace, sans-serif" : "inherit",
                  }}
                >
                  {val}
                </td>
              );
            })}
          </tr>
        );
      }
    }
  }

  report.Rows.forEach((r) => renderRow(r));

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

// ─── Not Connected Banner ─────────────────────────────────────────────────────

const PRACTICES = [
  { key: "apx",     name: "APX",     desc: "APX Advisors LLC",  xeroEmail: "info@apx-advisors.com" },
  { key: "apx-hrb", name: "APX-HRB", desc: "APX \u2013 HRB",         xeroEmail: "apxadvisor@gmail.com" },
  { key: "apx-bbn", name: "APX-BBN", desc: "BBN NETWORK",        xeroEmail: "citifynanc@gmail.com" },
] as const;

// Roles allowed to Connect / Disconnect Xero practices
const XERO_MANAGE_ROLES = ["super_admin", "admin", "controller", "bookkeeping_manager"] as const;
type XeroManageRole = typeof XERO_MANAGE_ROLES[number];

/** Returns minutes until token expiry, or null if no expiry date. Negative = already expired. */
function minutesUntilExpiry(expiresAt: Date | null): number | null {
  if (!expiresAt) return null;
  return (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60);
}
/** @deprecated use minutesUntilExpiry */
function hoursUntilExpiry(expiresAt: Date | null): number | null {
  const m = minutesUntilExpiry(expiresAt);
  return m === null ? null : m / 60;
}

type PracticeKey = "apx" | "apx-hrb" | "apx-bbn";

interface PracticeStatus {
  practiceKey: string;
  practiceName: string;
  connected: boolean;
  tenantName: string;
  tenantId: string;
  expiresAt: Date | null;
  updatedAt: Date | null;
  connectedAt: Date | null;
}

function PracticeConnectionPanel({
  statuses,
  onConnect,
  onReconnect,
  onDisconnect,
  onSelectPractice,
  selectedPractice,
  callerEmail,
  canManage,
}: {
  statuses: PracticeStatus[];
  onConnect: (key: PracticeKey) => void;
  onReconnect: (key: PracticeKey) => void;
  onDisconnect: (key: PracticeKey) => void;
  onSelectPractice: (key: PracticeKey) => void;
  selectedPractice: PracticeKey | null;
  callerEmail: string;
  canManage: boolean;
}) {
  return (
    <div style={{ padding: "2rem" }}>
      {/* Section header */}
      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
          <h3 style={{ fontSize: "16px", fontWeight: 700, color: "oklch(0.18 0.02 240)", margin: 0 }}>
            Connect Xero Practice Accounts
          </h3>
          <span style={{
            fontSize: "10px", fontWeight: 700, letterSpacing: "0.07em",
            padding: "2px 8px", borderRadius: "4px", textTransform: "uppercase",
            background: "oklch(0.72 0.14 75 / 15%)", color: "oklch(0.42 0.12 75)",
            border: "1px solid oklch(0.72 0.14 75 / 35%)"
          }}>
            Practice Accounts Only
          </span>
        </div>
        <p style={{ fontSize: "13px", color: "oklch(0.14 0.04 240)", marginBottom: "0.75rem" }}>
          Each practice connects independently. Click <strong style={{ color: "oklch(0.08 0.01 240)" }}>Reconnect</strong> on a card — you will be shown which Xero account to use before proceeding.
        </p>
        <div style={{
          fontSize: "12px", padding: "8px 12px", borderRadius: "6px",
          background: "oklch(0.97 0.03 60)", border: "1px solid oklch(0.88 0.08 60)",
          color: "oklch(0.38 0.10 55)", lineHeight: "1.5"
        }}>
          <strong>⚠ Important:</strong> Only connect the three APX practice accounts listed below (APX, APX-HRB, APX-BBN). Do <strong>not</strong> connect individual client entities — all client organisations are accessed automatically through the practice account tokens.
        </div>
      </div>

      {/* Practice cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1rem" }}>
        {PRACTICES.map((p) => {
          const status = statuses.find((s) => s.practiceKey === p.key);
          const connected = status?.connected ?? false;
          const isSelected = selectedPractice === p.key;

          return (
            <div
              key={p.key}
              style={{
                background: connected
                  ? "oklch(0.17 0.04 145 / 0.3)"
                  : "oklch(0.97 0.005 85)",
                border: connected
                  ? "1.5px solid oklch(0.35 0.01 240)"
                  : isSelected
                  ? "1.5px solid oklch(0.82 0.01 240)"
                  : "1px solid oklch(0.97 0.005 85)",
                borderRadius: "12px",
                padding: "1.25rem 1.5rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {/* Practice name + status badge */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={connected ? "oklch(0.82 0.01 240)" : "oklch(0.82 0.01 240)"} strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span style={{ fontSize: "15px", fontWeight: 700, color: "oklch(0.18 0.02 240)" }}>{p.name}</span>
                </div>
                {connected ? (
                  <span style={{
                    fontSize: "13px", fontWeight: 600, padding: "2px 8px",
                    background: "oklch(0.92 0.005 240 / 0.15)",
                    color: "oklch(0.08 0.01 240)",
                    borderRadius: "999px", border: "1px solid oklch(0.92 0.005 240 / 0.3)",
                  }}>
                    Connected
                  </span>
                ) : (
                  <span style={{
                    fontSize: "13px", fontWeight: 600, padding: "2px 8px",
                    background: "oklch(0.55 0.02 240 / 0.15)",
                    color: "oklch(0.14 0.04 240)",
                    borderRadius: "999px", border: "1px solid oklch(0.55 0.02 240 / 0.3)",
                  }}>
                    Not Connected
                  </span>
                )}
              </div>

              {/* Practice description / tenant name */}
              <p style={{ fontSize: "13px", color: "oklch(0.14 0.04 240)", margin: 0 }}>
                {connected && status?.tenantName ? status.tenantName : p.desc}
              </p>

              {/* Xero login email hint — always visible so users know which account to use */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="oklch(0.35 0.01 240)" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                <span style={{ fontSize: "13px", color: "oklch(0.08 0.01 240)", fontFamily: "monospace" }}>
                  {p.xeroEmail}
                </span>
              </div>

              {/* Last connected */}
              {connected && status?.updatedAt && (
                <p style={{ fontSize: "13px", color: "oklch(0.28 0.03 240)", margin: 0 }}>
                  Last connected: {new Date(status.updatedAt).toLocaleString()}
                </p>
              )}

              {/* ── Token expiry warning banner ── */}
              {connected && (() => {
                const minutes = minutesUntilExpiry(status?.expiresAt ?? null);
                if (minutes === null) return null;
                const isExpired = minutes <= 0;
                // Show warning when under 45 minutes (matches server-side refresh window)
                const isWarning = minutes > 0 && minutes <= 45;
                if (!isExpired && !isWarning) return null;
                const hours = minutes / 60;
                return (
                  <div style={{
                    padding: "10px 12px",
                    background: isExpired ? "oklch(0.96 0.03 25)" : "oklch(0.96 0.02 75)",
                    border: `1px solid ${isExpired ? "oklch(0.78 0.08 25)" : "oklch(0.78 0.06 75)"}`,
                    borderRadius: "8px",
                    display: "flex", flexDirection: "column", gap: "8px",
                  }}>
                    {/* Warning text row */}
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke={isExpired ? "oklch(0.55 0.01 240)" : "oklch(0.82 0.01 240)"}
                        strokeWidth="2" style={{ marginTop: 1, flexShrink: 0 }}>
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      <div>
                        <p style={{ margin: 0, fontSize: "13px", fontWeight: 600,
                          color: isExpired ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)" }}>
                          {isExpired ? "Token expired" : minutes < 60 ? `Token expires in ${Math.ceil(minutes)} min` : `Token expires in ${Math.ceil(hours)}h`}
                        </p>
                        <p style={{ margin: 0, fontSize: "13px",
                          color: isExpired ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)" }}>
                          {isExpired
                            ? "Reports will fail. Reconnect this practice to restore access."
                            : "Reconnect soon to avoid interruption to financial reports."}
                        </p>
                      </div>
                    </div>
                    {/* Reconnect now shortcut — only for users who can manage */}
                    {canManage && (
                      <button
                        onClick={() => onReconnect(p.key)}
                        style={{
                          alignSelf: "flex-start",
                          padding: "5px 10px",
                          fontSize: "13px", fontWeight: 600,
                          background: isExpired ? "oklch(0.92 0.005 240 / 0.2)" : "oklch(0.92 0.005 240 / 0.15)",
                          color: isExpired ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)",
                          border: `1px solid ${isExpired ? "oklch(0.92 0.005 240 / 0.5)" : "oklch(0.92 0.005 240 / 0.4)"}`,
                          borderRadius: "6px", cursor: "pointer",
                          display: "flex", alignItems: "center", gap: "5px",
                        }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reconnect now
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Action buttons */}
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
                {connected ? (
                  <>
                    <button
                      onClick={() => onSelectPractice(p.key)}
                      style={{
                        flex: 1, padding: "8px 12px", fontSize: "13px", fontWeight: 600,
                        background: isSelected ? "oklch(0.82 0.01 240)" : "oklch(0.97 0.005 85)",
                        color: isSelected ? "oklch(0.08 0.01 240)" : "oklch(0.12 0.04 240)",
                        border: isSelected ? "none" : "1px solid oklch(0.92 0.005 240 / 0.4)",
                        borderRadius: "8px", cursor: "pointer",
                      }}
                    >
                      {isSelected ? "✓ Viewing Reports" : "View Reports"}
                    </button>
                    {canManage && (
                      <button
                        onClick={() => onDisconnect(p.key)}
                        style={{
                          padding: "8px 12px", fontSize: "13px", fontWeight: 500,
                          background: "transparent",
                          color: "oklch(0.08 0.01 240)",
                          border: "1px solid oklch(0.92 0.005 240 / 0.4)",
                          borderRadius: "8px", cursor: "pointer",
                        }}
                      >
                        Disconnect
                      </button>
                    )}
                  </>
                ) : canManage ? (
                  <button
                    onClick={() => onConnect(p.key)}
                    style={{
                      flex: 1, padding: "8px 12px", fontSize: "13px", fontWeight: 600,
                      background: "oklch(0.82 0.01 240)",
                      color: "oklch(0.12 0.04 240)",
                      border: "none", borderRadius: "8px", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                    </svg>
                    Connect Practice
                  </button>
                ) : (
                  <p style={{ margin: 0, fontSize: "13px", color: "oklch(0.28 0.03 240)", fontStyle: "italic" }}>
                    Not connected — contact an admin to connect.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function XeroReports() {
  const [activeTab, setActiveTab] = useState<ReportTab>("profit-loss");
  // selectedTenantId removed — tenantId now comes directly from selectedClient.xeroTenantId
  const [bsDate, setBsDate] = useState(priorMonthEnd);
  const [plFromDate, setPlFromDate] = useState(firstOfYear);
  const [plToDate, setPlToDate] = useState(priorMonthEnd);
  const [tbDate, setTbDate] = useState(today());
  const [exDate, setExDate] = useState(today());
  const [csFromDate, setCsFromDate] = useState(firstOfYear);
  const [csToDate, setCsToDate] = useState(priorMonthEnd);
  const [bsEnabled, setBsEnabled] = useState(false);
  const [plEnabled, setPlEnabled] = useState(false);
  const [tbEnabled, setTbEnabled] = useState(false);
  const [exEnabled, setExEnabled] = useState(false);
  const [csEnabled, setCsEnabled] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  // ─── Automation job state ─────────────────────────────────────────────────
  const [automationJobId, setAutomationJobId] = useState<string | null>(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [pendingJobInfo, setPendingJobInfo] = useState<{
    jobId: string;
    practiceKey: string;
    practiceEmail: string;
    clientName: string;
    fromDate: string;
    toDate: string;
  } | null>(null);

  // ─── Session cookie panel state ─────────────────────────────────────────────
  const [showCookiePanel, setShowCookiePanel] = useState(false);
  const [cookiePanelPractice, setCookiePanelPractice] = useState<PracticeKey | null>(null);
  const [cookieInputValue, setCookieInputValue] = useState("");
  // Multi-practice connection state
  const [selectedPractice, setSelectedPractice] = useState<PracticeKey | null>(null);
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole(user?.role ?? "bookkeeper");
  const callerEmail = user?.email ?? "";

  // Only admin/controller/super_admin may connect or disconnect practices
  const canManage = !!user && (XERO_MANAGE_ROLES as readonly string[]).includes(user.role);

  // tRPC utils for imperative fetching
  const trpcUtils = trpc.useUtils();

  // ─── Client search state ─────────────────────────────────────────────────────
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<{
    id: number;
    name: string;
    coreName: string;
    practiceKey: string;
    xeroTenantId: string | null;
    isActive: boolean;
  } | null>(null);

  // Debounced search — only fire after 300ms of no typing
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(clientSearchQuery), 300);
    return () => clearTimeout(t);
  }, [clientSearchQuery]);

  // Always enabled — quickSearch is a cheap DB read and pre-loading avoids a
  // visible loading flash when the user first opens the combobox dropdown.
  const clientSearchResults = trpc.xeroPm.quickSearch.useQuery(
    { query: debouncedQuery },
    {
      enabled: true,
      staleTime: 60_000,
    }
  );

  // When a client changes, reset all report-enabled flags so stale data is cleared
  useEffect(() => {
    setBsEnabled(false);
    setPlEnabled(false);
    setTbEnabled(false);
    setExEnabled(false);
    setCsEnabled(false);
  }, [selectedClient]);

  // Sync coreClients mutation
  const utils = trpc.useUtils();
  const syncClientsMut = trpc.xeroPm.syncCoreClients.useMutation({
    onSuccess: (result: unknown) => {
      const r = result as { total?: number; perPractice?: Array<{ practiceKey: string; count: number; error?: string }> } | undefined;
      // Invalidate client search so the dropdown immediately shows newly linked clients
      utils.xeroPm.quickSearch.invalidate();
      // Build a summary of what was synced per practice
      const summary = r?.perPractice
        ?.map(p => `${p.practiceKey.toUpperCase()}: ${p.count}${p.error ? " (error)" : ""}`)
        .join(" · ");
      toast.success(`Xero orgs synced — ${r?.total ?? 0} total`, {
        description: summary ?? "Client list updated. Clients with matching names are now linked.",
        duration: 6000,
      });
    },
    onError: (err) => toast.error("Sync failed", { description: err.message }),
  });

  // ─── PM OAuth callback handling (?pm_auth=success|denied|error&practice=...) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pmResult = params.get("pm_auth");
    const pmPractice = params.get("practice");
    if (pmResult === "success") {
      const practiceName = PRACTICES.find((p) => p.key === pmPractice)?.name ?? pmPractice ?? "Practice";
      toast.success(`${practiceName} Practice Manager connected`, {
        description: "PM token saved. You can now Sync Xero Orgs to populate client tenant IDs.",
        duration: 8000,
      });
      // Invalidate PM statuses so the badge updates immediately
      utils.xeroPm.getPmStatuses.invalidate();
      window.history.replaceState({}, "", "/xero-reports");
    } else if (pmResult === "denied") {
      toast.error("PM access denied", { description: "You cancelled the Xero Practice Manager authorisation." });
      window.history.replaceState({}, "", "/xero-reports");
    } else if (pmResult === "error") {
      const reason = params.get("reason");
      toast.error("PM connection failed", {
        description: reason ? `Error: ${reason}` : "An error occurred during PM authorisation. Please try again.",
        duration: 10000,
      });
      window.history.replaceState({}, "", "/xero-reports");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Xero OAuth callback URL params (?xero_auth=success&practice=apx)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authResult = params.get("xero_auth");
    const practice = params.get("practice");
    if (authResult === "success") {
      const practiceName = PRACTICES.find((p) => p.key === practice)?.name ?? practice ?? "Practice";
      toast.success(`${practiceName} connected successfully`, {
        description: "Xero account linked. You can now pull financial reports.",
      });
      // Auto-select the newly connected practice
      if (practice) setSelectedPractice(practice as PracticeKey);
      // Clean the URL
      window.history.replaceState({}, "", "/xero-reports");
    } else if (authResult === "denied") {
      toast.error("Xero access denied", { description: "You cancelled the Xero authorisation." });
      window.history.replaceState({}, "", "/xero-reports");
    } else if (authResult === "error") {
      const reason = params.get("reason");
      toast.error("Xero connection failed", {
        description: reason
          ? `Error: ${reason}`
          : "An error occurred during authorisation. Please try again.",
        duration: 10000,
      });
      window.history.replaceState({}, "", "/xero-reports");
    }
  }, []);

  // All practice statuses — auto-refresh every 5 minutes so expiry banners update without manual reload
  // PM token statuses — separate OAuth from accounting tokens
  const pmStatusesQuery = trpc.xeroPm.getPmStatuses.useQuery(undefined, {
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
  });
  const pmStatuses = pmStatusesQuery.data ?? [];

  const practiceStatusesQuery = trpc.xero.practiceStatuses.useQuery(undefined, {
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    staleTime: 4 * 60 * 1000,       // consider data stale after 4 minutes
  });
  const practiceStatuses = practiceStatusesQuery.data ?? [];

  // Auto-select first connected practice
  useEffect(() => {
    if (!selectedPractice && practiceStatuses.length > 0) {
      const first = practiceStatuses.find((p) => p.connected);
      if (first) setSelectedPractice(first.practiceKey as PracticeKey);
    }
  }, [practiceStatuses, selectedPractice]);

  // Owner notification check — fires on mount and every 5 minutes alongside the status refresh
  const checkExpiryMut = trpc.xero.checkExpiryNotifications.useMutation();
  useEffect(() => {
    // Run immediately on mount, then every 5 minutes
    checkExpiryMut.mutate();
    const interval = setInterval(() => checkExpiryMut.mutate(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disconnect mutation
  const disconnectMut = trpc.xero.disconnectPractice.useMutation({
    onSuccess: () => {
      toast.success("Practice disconnected");
      practiceStatusesQuery.refetch();
      setSelectedPractice(null);
    },
    onError: (err) => toast.error("Disconnect failed", { description: err.message }),
  });

  // Legacy single-practice status (for backwards compat with report queries)
  const xeroStatus = trpc.xero.status.useQuery();
  const selectedPracticeStatus = practiceStatuses.find((p) => p.practiceKey === selectedPractice);
  const isConnected = selectedPracticeStatus?.connected ?? false;
  const anyConnected = practiceStatuses.some((p) => p.connected);

  // Connect handler — build the Xero OAuth URL client-side to avoid server round-trip
  // (the server tRPC call was hitting a platform rate limit returning plain-text "Rate exceeded")
  function handleConnect(key: PracticeKey) {
    const clientId = import.meta.env.VITE_XERO_CLIENT_ID as string | undefined;
    if (!clientId) {
      toast.error("Xero not configured", { description: "VITE_XERO_CLIENT_ID is not set. Please contact admin." });
      return;
    }
    const XERO_SCOPES = [
      "openid", "profile", "email", "offline_access",
      "accounting.invoices.read", "accounting.payments.read",
      "accounting.banktransactions.read", "accounting.manualjournals.read",
      "accounting.contacts.read",
      "accounting.reports.balancesheet.read",
      "accounting.reports.profitandloss.read",
      "accounting.reports.trialbalance.read",
      "accounting.reports.executivesummary.read",
      "accounting.reports.banksummary.read",
      "accounting.reports.budgetsummary.read",
      "accounting.reports.aged.read",
      "accounting.settings.read",
    ].join(" ");
    const params = new URLSearchParams({
      response_type: "code",
      client_id: clientId,
      redirect_uri: "https://www.apxsystem.io/api/xero/callback",
      scope: XERO_SCOPES,
      state: `apx-xero-${key}`,
    });
    window.location.href = `https://login.xero.com/identity/connect/authorize?${params.toString()}`;
  }

  // Disconnect handler
  function handleDisconnect(key: PracticeKey) {
    disconnectMut.mutate({ callerEmail, practiceKey: key });
  }

  // tenantId must come from the client's own xeroTenantId only.
  // Never fall back to the practice entity — that would show the wrong org's financials.
  const reportPracticeKey = (selectedClient?.practiceKey ?? "apx") as "apx" | "apx-hrb" | "apx-bbn";
  const tenantId = selectedClient?.xeroTenantId ?? "";
  // Flag: true when client is selected but has no Xero tenant ID configured
  const clientMissingTenantId = !!selectedClient && !selectedClient.xeroTenantId;

  // ─── Reconnect safeguard dialog state ────────────────────────────────────────
  const [reconnectConfirm, setReconnectConfirm] = useState<{ key: PracticeKey; name: string; email: string } | null>(null);

  function handleReconnectClick(key: PracticeKey) {
    const practice = PRACTICES.find((p) => p.key === key);
    if (!practice) return;
    setReconnectConfirm({ key, name: practice.name, email: practice.xeroEmail });
  }

  async function handleReconnectConfirmed() {
    if (!reconnectConfirm) return;
    setReconnectConfirm(null);
    await handleConnect(reconnectConfirm.key);
  }

  // Report queries — only fire when user clicks "Run Report"
  // tenantId and reportPracticeKey always come from selectedClient (never the practice itself)
  const bsQuery = trpc.xero.getBalanceSheet.useQuery(
    { tenantId, practiceKey: reportPracticeKey, date: bsDate, standardLayout: true },
    { enabled: bsEnabled && !!tenantId && !!selectedClient }
  );
  useEffect(() => {
    if (bsQuery.error) {
      toast.error("Balance Sheet failed", { description: bsQuery.error.message });
      setBsEnabled(false);
    }
  }, [bsQuery.error]);

  const plQuery = trpc.xero.getProfitAndLoss.useQuery(
    { tenantId, practiceKey: reportPracticeKey, fromDate: plFromDate, toDate: plToDate, standardLayout: true },
    { enabled: plEnabled && !!tenantId && !!selectedClient }
  );
  useEffect(() => {
    if (plQuery.error) {
      toast.error("P&L report failed", { description: plQuery.error.message });
      setPlEnabled(false);
    }
  }, [plQuery.error]);

  const tbQuery = trpc.xero.getTrialBalance.useQuery(
    { tenantId, practiceKey: reportPracticeKey, date: tbDate },
    { enabled: tbEnabled && !!tenantId && !!selectedClient }
  );
  useEffect(() => {
    if (tbQuery.error) {
      toast.error("Trial Balance failed", { description: tbQuery.error.message });
      setTbEnabled(false);
    }
  }, [tbQuery.error]);

  const exQuery = trpc.xero.getExecutiveSummary.useQuery(
    { tenantId, practiceKey: reportPracticeKey, date: exDate },
    { enabled: exEnabled && !!tenantId && !!selectedClient }
  );
  useEffect(() => {
    if (exQuery.error) {
      toast.error("Executive Summary failed", { description: exQuery.error.message });
      setExEnabled(false);
    }
  }, [exQuery.error]);

  const csQuery = trpc.xero.getCashSummary.useQuery(
    { tenantId, practiceKey: reportPracticeKey, fromDate: csFromDate, toDate: csToDate },
    { enabled: csEnabled && !!tenantId && !!selectedClient }
  );
  useEffect(() => {
    if (csQuery.error) {
      toast.error("Cash Summary failed", { description: csQuery.error.message });
      setCsEnabled(false);
    }
  }, [csQuery.error]);

  // ─── Automation mutations ─────────────────────────────────────────────────
  const createJobMut = trpc.xeroAutomation.createJob.useMutation({
    onSuccess: (data, variables) => {
      const practiceConfig = PRACTICES.find((p) => p.key === variables.practiceKey);
      setPendingJobInfo({
        jobId: data.jobId,
        practiceKey: variables.practiceKey,
        practiceEmail: practiceConfig?.xeroEmail ?? variables.practiceKey,
        clientName: variables.clientName,
        fromDate: variables.fromDate,
        toDate: variables.toDate,
      });
      setShowApprovalDialog(true);
    },
    onError: (err) => toast.error("Failed to create report job", { description: err.message }),
  });

  const approveJobMut = trpc.xeroAutomation.approveJob.useMutation({
    onSuccess: (_, variables) => {
      setShowApprovalDialog(false);
      setAutomationJobId(variables.jobId);
      toast.success("Report generation started", {
        description: "Manus is now logging into Xero and generating reports. This may take 1–2 minutes.",
        duration: 8000,
      });
    },
    onError: (err) => toast.error("Approval failed", { description: err.message }),
  });

  // Poll job status while running
  const jobStatusQuery = trpc.xeroAutomation.getJobStatus.useQuery(
    { jobId: automationJobId ?? "" },
    {
      enabled: !!automationJobId,
      refetchInterval: (query) => {
        const status = (query.state.data as { status?: string } | undefined)?.status;
        if (status === "completed" || status === "failed") return false;
        return 3000;
      },
    }
  );

  // When job completes, show success/failure toast
  useEffect(() => {
    const job = jobStatusQuery.data as { status?: string; clientName?: string; error?: string } | undefined;
    if (!job) return;
    if (job.status === "completed") {
      toast.success(`Reports ready for ${job.clientName}`, {
        description: "All reports have been generated and saved.",
        duration: 10000,
      });
      setAutomationJobId(null);
    } else if (job.status === "failed") {
      toast.error(`Report generation failed for ${job.clientName}`, {
        description: job.error ?? "Unknown error. Please try again.",
        duration: 10000,
      });
      setAutomationJobId(null);
    }
   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(jobStatusQuery.data as { status?: string } | undefined)?.status]);
  const sessionStatusQuery = trpc.xeroAutomation.getSessionStatus.useQuery(undefined, {
    refetchInterval: 60_000,
  });
  const sessionStatusMap = Object.fromEntries(
    (sessionStatusQuery.data ?? []).map((s) => [s.practiceKey, s])
  );
  const saveSessionCookiesMut = trpc.xeroAutomation.saveSessionCookies.useMutation({
    onSuccess: (data) => { toast.success(data.message); sessionStatusQuery.refetch(); },
    onError: (err) => toast.error("Failed to save cookies", { description: err.message }),
  });

  function runReport() {
    if (!selectedClient) {
      toast.error("Please select a client first.", { description: "Use the Client search box to find and select a client before running a report." });
      return;
    }
    // If client has no xeroTenantId, use the web automation path
    if (!selectedClient.xeroTenantId) {
      const practiceKey = (selectedClient.practiceKey ?? "apx") as "apx" | "apx-hrb" | "apx-bbn";
      const fromDate = (activeTab === "balance-sheet" || activeTab === "trial-balance") ? bsDate : plFromDate;
      const toDate = (activeTab === "balance-sheet" || activeTab === "trial-balance") ? bsDate : plToDate;
      createJobMut.mutate({
        practiceKey,
        clientName: selectedClient.name,
        fromDate,
        toDate,
      });
      return;
    }
    if (activeTab === "balance-sheet") {
      setBsEnabled(true);
      bsQuery.refetch();
    } else if (activeTab === "profit-loss") {
      setPlEnabled(true);
      plQuery.refetch();
    } else if (activeTab === "trial-balance") {
      setTbEnabled(true);
      tbQuery.refetch();
    } else if (activeTab === "cash-summary") {
      setCsEnabled(true);
      csQuery.refetch();
    } else if (activeTab === "executive-summary") {
      setExEnabled(true);
      exQuery.refetch();
    }
  }

  function handlePrint() {
    window.print();
  }

  const activeReport: XeroReport | undefined =
    activeTab === "balance-sheet"
      ? bsQuery.data
      : activeTab === "profit-loss"
      ? plQuery.data
      : activeTab === "trial-balance"
      ? tbQuery.data
      : activeTab === "cash-summary"
      ? csQuery.data
      : exQuery.data;

  const isLoading =
    activeTab === "balance-sheet"
      ? bsQuery.isFetching
      : activeTab === "profit-loss"
      ? plQuery.isFetching
      : activeTab === "trial-balance"
      ? tbQuery.isFetching
      : activeTab === "cash-summary"
      ? csQuery.isFetching
      : exQuery.isFetching;

  const s = {
    page: {
      padding: "24px",
      maxWidth: 1100,
      margin: "0 auto",
    } as React.CSSProperties,
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "24px",
      flexWrap: "wrap" as const,
      gap: "12px",
    } as React.CSSProperties,
    title: {
      fontSize: "22px",
      fontWeight: 700,
      color: "oklch(0.18 0.02 240)",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    } as React.CSSProperties,
    card: {
      background: "oklch(0.94 0.018 220)",
      border: "1px solid oklch(0.97 0.005 85)",
      borderRadius: "12px",
      overflow: "clip",
      overflowClipMargin: "0px",
    } as React.CSSProperties,
    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "16px 20px",
      borderBottom: "1px solid oklch(0.97 0.005 85)",
      flexWrap: "wrap" as const,
      background: "oklch(0.94 0.018 220)",
    } as React.CSSProperties,
    label: {
      fontSize: "13px",
      color: "oklch(0.14 0.04 240)",
      marginBottom: "4px",
      display: "block",
    } as React.CSSProperties,
    input: {
      background: "oklch(0.93 0.008 240)",
      border: "1px solid oklch(0.88 0.01 240)",
      borderRadius: "6px",
      padding: "7px 10px",
      fontSize: "13px",
      color: "oklch(0.20 0.02 240)",
      outline: "none",
      colorScheme: "light",
    } as React.CSSProperties,
    select: {
      background: "oklch(0.93 0.008 240)",
      border: "1px solid oklch(0.88 0.01 240)",
      borderRadius: "6px",
      padding: "7px 10px",
      fontSize: "13px",
      color: "oklch(0.20 0.02 240)",
      outline: "none",
      cursor: "pointer",
      minWidth: 200,
    } as React.CSSProperties,
    runBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 18px",
      background: "oklch(0.82 0.01 240)",
      color: "oklch(0.12 0.04 240)",
      borderRadius: "7px",
      fontSize: "13px",
      fontWeight: 600,
      border: "none",
      cursor: "pointer",
      transition: "opacity 0.15s",
      marginLeft: "auto",
    } as React.CSSProperties,
    printBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 14px",
      background: "oklch(0.94 0.018 220)",
      color: "oklch(0.14 0.04 240)",
      borderRadius: "7px",
      fontSize: "13px",
      fontWeight: 500,
      border: "1px solid oklch(0.88 0.01 240)",
      cursor: "pointer",
      transition: "background 0.15s",
    } as React.CSSProperties,
    tabs: {
      display: "flex",
      gap: "2px",
      padding: "12px 20px 0",
      borderBottom: "1px solid oklch(0.97 0.005 85)",
      background: "oklch(0.94 0.018 220)",
      overflowX: "auto",
      flexWrap: "nowrap",
    } as React.CSSProperties,
    tab: (active: boolean): React.CSSProperties => ({
      padding: "9px 16px",
      fontSize: "13px",
      fontWeight: active ? 600 : 400,
      color: active ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)",
      cursor: "pointer",
      background: "none",
      border: "none",
      borderBottom: active ? "2px solid oklch(0.82 0.01 240)" : "2px solid transparent",
      transition: "color 0.15s",
      whiteSpace: "nowrap" as const,
    }),
    reportHeader: {
      padding: "20px 20px 12px",
      borderBottom: "1px solid oklch(0.97 0.005 85)",
    } as React.CSSProperties,
    reportTitle: {
      fontSize: "18px",
      fontWeight: 700,
      color: "oklch(0.18 0.02 240)",
      marginBottom: "4px",
    } as React.CSSProperties,
    reportMeta: {
      fontSize: "13px",
      color: "oklch(0.14 0.04 240)",
    } as React.CSSProperties,
    loadingState: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "3rem",
      gap: "12px",
      color: "oklch(0.14 0.04 240)",
      fontSize: "14px",
    } as React.CSSProperties,
    emptyState: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      padding: "3rem",
      gap: "12px",
      color: "oklch(0.28 0.03 240)",
      fontSize: "14px",
      textAlign: "center" as const,
    } as React.CSSProperties,
    connectedBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 10px",
      background: "oklch(0.92 0.005 240 / 15%)",
      border: "1px solid oklch(0.92 0.005 240 / 30%)",
      borderRadius: "20px",
      fontSize: "13px",
      color: "oklch(0.08 0.01 240)",
    } as React.CSSProperties,
  };

  const TABS: { id: ReportTab; label: string }[] = [
    { id: "profit-loss", label: "Profit & Loss" },
    { id: "balance-sheet", label: "Balance Sheet" },
    { id: "trial-balance", label: "Trial Balance" },
    { id: "cash-summary", label: "Cash Summary" },
    { id: "executive-summary", label: "Executive Summary" },
  ];

  return (
    <DashboardLayout>
      <div style={s.page}>
        {/* Page header */}
        <div style={s.header}>
          <div style={s.title}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="oklch(0.82 0.01 240)" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Financial Reports
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" as const }}>
            {/* ── 3-entity Xero practice connection cards ── */}
            {PRACTICES.map((p) => {
              const pStatus = practiceStatuses.find((ps) => ps.practiceKey === p.key);
              const pConnected = pStatus?.connected ?? false;
              const mins = pConnected ? minutesUntilExpiry(pStatus?.expiresAt ?? null) : null;
              const pExpired = mins !== null && mins <= 0;
              const pExpiringSoon = mins !== null && mins > 0 && mins <= 60 * 24;
              // "refreshing" = access token expired but refresh token is still valid (<58 days since connect).
              // The auto-refresh scheduler runs every 20 min and will fix this automatically.
              // Only show true "EXPIRED" (red) when the refresh token itself is dead (>58 days) or no connectedAt.
              const connectedAtMs = pStatus?.connectedAt ? new Date(pStatus.connectedAt).getTime() : null;
              const daysSinceConnect = connectedAtMs ? (Date.now() - connectedAtMs) / (1000 * 60 * 60 * 24) : null;
              const refreshTokenStillValid = daysSinceConnect !== null && daysSinceConnect < 58;
              const isSoftExpiry = pExpired && pConnected && refreshTokenStillValid;
              type PillState = "connected" | "expiring" | "expired" | "refreshing" | "disconnected";
              const state: PillState = !pConnected ? "disconnected" : pExpired ? (isSoftExpiry ? "refreshing" : "expired") : pExpiringSoon ? "expiring" : "connected";

              // Format expiry countdown
              const expiryLabel = (() => {
                if (!pConnected || mins === null) return null;
                if (mins <= 0) return "Expired";
                if (mins < 60) return `${Math.ceil(mins)}m left`;
                const h = Math.floor(mins / 60);
                const d = Math.floor(h / 24);
                if (d >= 1) return `${d}d ${h % 24}h left`;
                return `${h}h ${Math.floor(mins % 60)}m left`;
              })();

              const expiryDate = pStatus?.expiresAt ? new Date(pStatus.expiresAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : null;

              const stateCfg = {
                connected: {
                  cardBg: "oklch(0.96 0.005 240)",
                  cardBorder: "oklch(0.78 0.01 240)",
                  cardShadow: "none",
                  dot: "oklch(0.35 0.01 240)",
                  dotGlow: "transparent",
                  labelBg: "oklch(0.90 0.008 240)",
                  labelColor: "oklch(0.08 0.01 240)",
                  labelText: "CONNECTED",
                  nameColor: "oklch(0.08 0.01 240)",
                  expiryColor: "oklch(0.35 0.01 240)",
                  btnBg: "oklch(0.90 0.008 240)",
                  btnColor: "oklch(0.08 0.01 240)",
                  btnBorder: "oklch(0.78 0.01 240)",
                  btnLabel: "Reconnect",
                },
                expiring: {
                  cardBg: "oklch(0.96 0.005 240)",
                  cardBorder: "oklch(0.78 0.01 240)",
                  cardShadow: "none",
                  dot: "oklch(0.35 0.01 240)",
                  dotGlow: "transparent",
                  labelBg: "oklch(0.90 0.008 240)",
                  labelColor: "oklch(0.08 0.01 240)",
                  labelText: "EXPIRING SOON",
                  nameColor: "oklch(0.08 0.01 240)",
                  expiryColor: "oklch(0.35 0.01 240)",
                  btnBg: "oklch(0.90 0.008 240)",
                  btnColor: "oklch(0.08 0.01 240)",
                  btnBorder: "oklch(0.78 0.01 240)",
                  btnLabel: "Reconnect Now",
                },
                expired: {
                  cardBg: "oklch(0.96 0.005 240)",
                  cardBorder: "oklch(0.78 0.01 240)",
                  cardShadow: "none",
                  dot: "oklch(0.35 0.01 240)",
                  dotGlow: "transparent",
                  labelBg: "oklch(0.88 0.005 240)",
                  labelColor: "oklch(0.08 0.01 240)",
                  labelText: "EXPIRED",
                  nameColor: "oklch(0.08 0.01 240)",
                  expiryColor: "oklch(0.35 0.01 240)",
                  btnBg: "oklch(0.90 0.008 240)",
                  btnColor: "oklch(0.08 0.01 240)",
                  btnBorder: "oklch(0.78 0.01 240)",
                  btnLabel: "Reconnect Now",
                },
                // "refreshing" = access token expired but refresh token valid — auto-refresh will fix within 20 min
                refreshing: {
                  cardBg: "oklch(0.96 0.005 240)",
                  cardBorder: "oklch(0.78 0.01 240)",
                  cardShadow: "none",
                  dot: "oklch(0.35 0.01 240)",
                  dotGlow: "transparent",
                  labelBg: "oklch(0.90 0.008 240)",
                  labelColor: "oklch(0.08 0.01 240)",
                  labelText: "REFRESHING",
                  nameColor: "oklch(0.08 0.01 240)",
                  expiryColor: "oklch(0.35 0.01 240)",
                  btnBg: "oklch(0.90 0.008 240)",
                  btnColor: "oklch(0.08 0.01 240)",
                  btnBorder: "oklch(0.78 0.01 240)",
                  btnLabel: "Force Reconnect",
                },
                disconnected: {
                  cardBg: "oklch(0.96 0.005 240)",
                  cardBorder: "oklch(0.82 0.01 240)",
                  cardShadow: "none",
                  dot: "oklch(0.55 0.01 240)",
                  dotGlow: "transparent",
                  labelBg: "oklch(0.90 0.008 240)",
                  labelColor: "oklch(0.35 0.01 240)",
                  labelText: "NOT CONNECTED",
                  nameColor: "oklch(0.35 0.01 240)",
                  expiryColor: "oklch(0.35 0.01 240)",
                  btnBg: "oklch(0.90 0.008 240)",
                  btnColor: "oklch(0.08 0.01 240)",
                  btnBorder: "oklch(0.78 0.01 240)",
                  btnLabel: "Connect",
                },
              }[state];

              return (
                <div key={p.key} style={{
                  display: "flex",
                  flexDirection: "column" as const,
                  gap: "6px",
                  padding: "10px 14px",
                  background: stateCfg.cardBg,
                  border: `1.5px solid ${stateCfg.cardBorder}`,
                  borderRadius: "12px",
                  minWidth: "160px",
                  boxShadow: stateCfg.cardShadow,
                }}>
                  {/* Row 1: dot + name + status label */}
                  <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                    <span style={{
                      width: 9, height: 9, borderRadius: "50%",
                      background: stateCfg.dot,
                      flexShrink: 0,
                      boxShadow: `0 0 6px ${stateCfg.dotGlow}`,
                      display: "inline-block",
                    }} />
                    <span style={{ fontWeight: 800, fontSize: "13px", color: stateCfg.nameColor, letterSpacing: "0.02em" }}>{p.name}</span>
                    <span style={{
                      marginLeft: "auto",
                      padding: "2px 7px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: 900,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase" as const,
                      background: stateCfg.labelBg,
                      color: stateCfg.labelColor,
                    }}>{stateCfg.labelText}</span>
                  </div>
                  {/* Row 2: expiry info */}
                  <div style={{ fontSize: "13px", color: stateCfg.expiryColor, lineHeight: 1.3 }}>
                    {pConnected && expiryLabel ? (
                      <span title={expiryDate ?? ""}>
                        {state === "expired"
                          ? "⚠ Token expired — reconnect required"
                          : state === "refreshing"
                          ? "↻ Auto-refreshing — ready within 20 min"
                          : `⏱ ${expiryLabel}${expiryDate ? ` · ${expiryDate}` : ""}`}
                      </span>
                    ) : !pConnected ? (
                      <span style={{ color: "oklch(0.28 0.04 240)" }}>No active token · {p.xeroEmail}</span>
                    ) : (
                      <span style={{ color: "oklch(0.08 0.01 240)" }}>Token active · {p.xeroEmail}</span>
                    )}
                  </div>
                  {/* Row 3: last-refresh timestamp + auto-refresh note or connect button */}
                  {state === "disconnected" && canManage ? (
                    <button
                      onClick={() => handleConnect(p.key as PracticeKey)}
                      style={{
                        marginTop: "2px",
                        padding: "5px 10px",
                        fontSize: "13px",
                        fontWeight: 700,
                        borderRadius: "8px",
                        border: `1px solid ${stateCfg.btnBorder}`,
                        background: stateCfg.btnBg,
                        color: stateCfg.btnColor,
                        cursor: "pointer",
                        letterSpacing: "0.02em",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        width: "100%",
                        justifyContent: "center",
                      }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                      </svg>
                      Connect Practice
                    </button>
                  ) : state !== "disconnected" ? (
                    <div style={{ marginTop: "2px", fontSize: "10px", color: state === "expired" ? "oklch(0.08 0.01 240)" : state === "refreshing" ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)", display: "flex", flexDirection: "column" as const, gap: "2px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {state === "expired"
                          ? "Auto-refresh failed — reconnect required"
                          : state === "refreshing"
                          ? "Auto-refreshing every 20 min (6 AM–9 PM PT) — no action needed"
                          : "Auto-refreshing every 20 min (6 AM–9 PM PT)"}
                      </div>
                      {pStatus?.updatedAt && (
                        <div style={{ color: state === "expired" ? "oklch(0.08 0.01 240)" : state === "refreshing" ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)", paddingLeft: "13px" }}>
                          Last refresh: {new Date(pStatus.updatedAt).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })} PT
                        </div>
                      )}
                      {/* Reconnect button for expired tokens */}
                      {state === "expired" && canManage && (
                        <button
                          onClick={() => handleReconnectClick(p.key as PracticeKey)}
                          style={{
                            marginTop: "4px",
                            padding: "6px 10px",
                            fontSize: "13px",
                            fontWeight: 800,
                            borderRadius: "8px",
                            border: "1.5px solid oklch(0.78 0.01 240)",
                            background: "oklch(0.90 0.008 240)",
                            color: "oklch(0.08 0.01 240)",
                            cursor: "pointer",
                            letterSpacing: "0.03em",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            width: "100%",
                            justifyContent: "center",
                            boxShadow: "none",
                          }}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M1 4v6h6" /><path d="M23 20v-6h-6" />
                            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15" />
                          </svg>
                          Reconnect Now
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {/* Sync Xero Orgs button — links all 3 practices' client orgs to CORE list */}
            {canManage && (
              <button
                onClick={() => syncClientsMut.mutate()}
                disabled={syncClientsMut.isPending}
                title="Fetch all client organisations from all 3 Xero practices and link them to the CORE client list"
                style={{
                  display: "inline-flex", alignItems: "center", gap: "5px",
                  padding: "5px 10px",
                  fontSize: "13px", fontWeight: 600,
                  borderRadius: "20px",
                  border: "1px solid oklch(0.78 0.01 240)",
                  background: "oklch(0.92 0.005 240)",
                  color: syncClientsMut.isPending ? "oklch(0.55 0.01 240)" : "oklch(0.08 0.01 240)",
                  cursor: syncClientsMut.isPending ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap" as const,
                }}
              >
                {syncClientsMut.isPending ? (
                  <div style={{ width: 10, height: 10, border: "1.5px solid oklch(0.78 0.01 240)", borderTopColor: "oklch(0.12 0.01 240)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M1 4v6h6" /><path d="M23 20v-6h-6" />
                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15" />
                  </svg>
                )}
                {syncClientsMut.isPending ? "Syncing…" : "Sync Clients to WorkCenter"}
              </button>
            )}
            {/* Print button */}
            {activeReport && (
              <button style={s.printBtn} onClick={handlePrint}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                Print / PDF
              </button>
            )}
          </div>
        </div>

        {/* ── Token Age Warning Banner ── */}
        {(() => {
          // Find the practice with the highest token age that is >= 50 days
          const agingPractices = practiceStatuses
            .filter((ps) => ps.connected && ps.connectedAt)
            .map((ps) => {
              const connectedAtMs = new Date(ps.connectedAt!).getTime();
              const ageDays = Math.floor((Date.now() - connectedAtMs) / (1000 * 60 * 60 * 24));
              const daysRemaining = 60 - ageDays;
              return { ...ps, ageDays, daysRemaining };
            })
            .filter((ps) => ps.ageDays >= 50 && ps.daysRemaining > 0)
            .sort((a, b) => b.ageDays - a.ageDays); // highest age first

          if (agingPractices.length === 0) return null;

          // Determine overall severity from the most-aged practice
          const worst = agingPractices[0];
          const severity = worst.ageDays >= 58 ? "critical" : worst.ageDays >= 55 ? "high" : "medium";
          const bannerAccent = severity === "critical" ? "#c0392b" : severity === "high" ? "#b7770d" : "#7a6a2a";
          const bannerBg = severity === "critical" ? "#fff5f5" : severity === "high" ? "#fffbf0" : "#fdfbf2";
          const bannerBorder = severity === "critical" ? "#fcc" : severity === "high" ? "#f5dfa0" : "#e8dfa0";
          const bannerIcon = severity === "critical" ? "🔴" : severity === "high" ? "🟠" : "🟡";
          const bannerTitle = severity === "critical"
            ? `Urgent: Xero token${agingPractices.length > 1 ? "s" : ""} expiring in ${worst.daysRemaining} day${worst.daysRemaining === 1 ? "" : "s"}`
            : severity === "high"
            ? `Warning: Xero token${agingPractices.length > 1 ? "s" : ""} expiring in ${worst.daysRemaining} days`
            : `Advisory: Xero token${agingPractices.length > 1 ? "s" : ""} approaching 60-day limit`;

          return (
            <div style={{
              margin: "0 0 10px 0",
              padding: "8px 14px",
              background: bannerBg,
              border: `1px solid ${bannerBorder}`,
              borderLeft: `3px solid ${bannerAccent}`,
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "12px",
              color: "#555",
            }}>
              <span style={{ fontSize: "14px", flexShrink: 0 }}>{bannerIcon}</span>
              <div style={{ flex: 1, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
                <span style={{ fontWeight: 600, color: bannerAccent }}>{bannerTitle}:</span>
                {agingPractices.map((p) => (
                  <span key={p.practiceKey} style={{ color: "#666" }}>
                    <strong style={{ color: bannerAccent }}>{p.practiceName}</strong> — {p.daysRemaining}d remaining
                  </span>
                ))}
                <span style={{ color: "#888" }}>· Reconnect from Xero Client Mgmt to reset.</span>
              </div>
            </div>
          );
        })()}

        <div style={s.card} ref={printRef}>
            {/* Toolbar */}
            <div style={s.toolbar}>
              {/* Client search combobox — powered by Xero /connections endpoint */}
              <div style={{ position: "relative", minWidth: 260 }}>
                <span style={s.label}>Client</span>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    placeholder="Search client name…"
                    value={selectedClient ? (selectedClient.coreName || selectedClient.name) : clientSearchQuery}
                    style={{
                      ...s.input,
                      minWidth: 260,
                      paddingRight: 32,
                    }}
                    onFocus={() => {
                      if (selectedClient) { setSelectedClient(null); setClientSearchQuery(""); }
                      setClientSearchOpen(true);
                    }}
                    onChange={(e) => {
                      setClientSearchQuery(e.target.value);
                      setSelectedClient(null);
                      setClientSearchOpen(true);
                    }}
                    onBlur={() => setTimeout(() => setClientSearchOpen(false), 200)}
                  />
                  {selectedClient && (
                    <button
                      onClick={() => { setSelectedClient(null); setClientSearchQuery(""); setClientSearchOpen(false); }}
                      style={{
                        position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                        background: "none", border: "none", cursor: "pointer",
                        color: "oklch(0.14 0.04 240)", fontSize: 16, lineHeight: 1, padding: 2,
                      }}
                    >×</button>
                  )}
                </div>
                {/* Dropdown results */}
                {clientSearchOpen && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                    background: "oklch(0.93 0.008 240)",
                    border: "1px solid oklch(0.88 0.01 240)",
                    borderRadius: "8px", marginTop: 4,
                    maxHeight: 280, overflowY: "auto",
                    boxShadow: "0 8px 24px oklch(0 0 0 / 0.4)",
                  }}>
                    {clientSearchResults.isLoading ? (
                      <div style={{ padding: "12px 14px", fontSize: 13, color: "oklch(0.14 0.04 240)" }}>Searching…</div>
                    ) : clientSearchResults.data?.length === 0 ? (
                      <div style={{ padding: "12px 14px", fontSize: 13, color: "oklch(0.28 0.03 240)" }}>
                        {clientSearchQuery ? `No clients found for "${clientSearchQuery}"` : "No clients found — click Sync Client List"}
                      </div>
                    ) : (
                      clientSearchResults.data?.map((client) => (
                        <button
                          key={client.id}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSelectedClient(client);
                            setClientSearchQuery("");
                            setClientSearchOpen(false);
                          }}
                          style={{
                            display: "block", width: "100%", textAlign: "left",
                            padding: "9px 14px", fontSize: 13,
                            background: "none", border: "none", cursor: "pointer",
                            color: "oklch(0.20 0.02 240)",
                            borderBottom: "1px solid oklch(0.22 0.04 240 / 0.5)",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "oklch(0.97 0.005 85)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                        >
                          <div style={{ fontWeight: 500 }}>{client.coreName || client.name}</div>
                          <div style={{ fontSize: 11, color: "oklch(0.08 0.01 240)", marginTop: 2 }}>
                            {client.practiceKey.toUpperCase()}{!client.xeroTenantId ? " · Not in Xero" : ""}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Date controls — vary by tab */}
              {activeTab === "balance-sheet" && (
                <div>
                  <span style={s.label}>As of Date</span>
                  <input
                    type="date"
                    style={s.input}
                    value={bsDate}
                    onChange={(e) => setBsDate(e.target.value)}
                  />
                </div>
              )}
              {activeTab === "profit-loss" && (
                <>
                  <div>
                    <span style={s.label}>From Date</span>
                    <input
                      type="date"
                      style={s.input}
                      value={plFromDate}
                      onChange={(e) => setPlFromDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <span style={s.label}>To Date</span>
                    <input
                      type="date"
                      style={s.input}
                      value={plToDate}
                      onChange={(e) => setPlToDate(e.target.value)}
                    />
                  </div>
                </>
              )}
              {activeTab === "trial-balance" && (
                <div>
                  <span style={s.label}>As of Date</span>
                  <input
                    type="date"
                    style={s.input}
                    value={tbDate}
                    onChange={(e) => setTbDate(e.target.value)}
                  />
                </div>
              )}
              {activeTab === "cash-summary" && (
                <>
                  <div>
                    <span style={s.label}>From Date</span>
                    <input
                      type="date"
                      style={s.input}
                      value={csFromDate}
                      onChange={(e) => setCsFromDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <span style={s.label}>To Date</span>
                    <input
                      type="date"
                      style={s.input}
                      value={csToDate}
                      onChange={(e) => setCsToDate(e.target.value)}
                    />
                  </div>
                </>
              )}
              {activeTab === "executive-summary" && (
                <div>
                  <span style={s.label}>As of Date</span>
                  <input
                    type="date"
                    style={s.input}
                    value={exDate}
                    onChange={(e) => setExDate(e.target.value)}
                  />
                </div>
              )}

              {/* Run button */}
              <button
                style={{
                  ...s.runBtn,
                  opacity: isLoading ? 0.7 : 1,
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
                onClick={runReport}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div
                      className="animate-spin"
                      style={{
                        width: 14,
                        height: 14,
                        border: "2px solid oklch(0.15 0.04 240 / 30%)",
                        borderTopColor: "oklch(0.97 0.005 85)",
                        borderRadius: "50%",
                      }}
                    />
                    Running…
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                    Run Report
                  </>
                )}
              </button>
            </div>

            {/* Tabs */}
            <div style={s.tabs}>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  style={s.tab(activeTab === tab.id)}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Blocking notice — shown when client has no xeroTenantId (cannot run reports) */}
            {clientMissingTenantId && (
              <div style={{
                background: "oklch(0.94 0.015 240)",
                border: "1px solid oklch(0.78 0.04 240)",
                borderRadius: "6px",
                padding: "12px 16px",
                marginBottom: "12px",
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                fontSize: "13px",
                color: "oklch(0.22 0.06 240)",
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="oklch(0.55 0.01 240)" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
                <div style={{ flex: 1 }}>
                  <strong style={{ color: "oklch(0.12 0.04 240)" }}>Xero not connected for this client.</strong>{" "}
                  This client does not have a Xero organisation linked yet.
                  {canManage && (
                    <>
                      {" "}Click{" "}
                      <button
                        onClick={() => syncClientsMut.mutate()}
                        disabled={syncClientsMut.isPending}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "4px",
                          padding: "2px 8px", fontSize: "13px", fontWeight: 600,
                          borderRadius: "12px",
                          border: "1px solid oklch(0.92 0.005 240 / 60%)",
                          background: "oklch(0.2 0.06 145 / 40%)",
                          color: "oklch(0.08 0.01 240)",
                          cursor: syncClientsMut.isPending ? "not-allowed" : "pointer",
                          verticalAlign: "middle",
                        }}
                      >
                        {syncClientsMut.isPending ? "Syncing…" : "Sync Clients to WorkCenter"}
                      </button>
                      {" "}to automatically link this client to their Xero organisation across all 3 practices.
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Report content */}
            <div className="print-area">
              {!selectedClient ? (
                <div style={{ ...s.loadingState, flexDirection: "column", gap: "12px", padding: "48px 24px" }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="oklch(0.45 0.02 240)" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                  </svg>
                  <div style={{ color: "oklch(0.14 0.04 240)", fontSize: "15px", fontWeight: 600 }}>Select a client to run reports</div>
                  <div style={{ color: "oklch(0.28 0.03 240)", fontSize: "13px", maxWidth: 340, textAlign: "center" }}>
                    Use the Client search box above to find one of your {clientSearchResults.data?.length ?? 0} client organisations, then click Run Report.
                  </div>
                </div>
              ) : isLoading ? (
                <div style={s.loadingState}>
                  <div
                    className="animate-spin"
                    style={{
                      width: 24,
                      height: 24,
                      border: "2px solid oklch(0.92 0.005 240 / 30%)",
                      borderTopColor: "oklch(0.82 0.01 240)",
                      borderRadius: "50%",
                    }}
                  />
                  Fetching report from Xero…
                </div>
              ) : activeReport ? (
                <>
                  {/* Report header */}
                  <div style={s.reportHeader} data-print-header="true">
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
                      <div>
                        <div style={s.reportTitle}>{activeReport.ReportName}</div>
                        {activeReport.ReportTitles?.map((t, i) => (
                          <div key={i} style={s.reportMeta}>{t}</div>
                        ))}
                        <div style={{ ...s.reportMeta, marginTop: "4px" }}>
                          Report Date: {activeReport.ReportDate}
                          {activeReport.UpdatedDateUTC && (() => {
                            const raw = activeReport.UpdatedDateUTC;
                            const match = raw.match(/\/Date\((\d+)([+-]\d+)?\)\//);
                            const d = match ? new Date(parseInt(match[1])) : new Date(raw);
                            return isNaN(d.getTime()) ? null : ` · Updated: ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                          })()}
                        </div>
                      </div>
                      {/* APX Branding */}
                      <div data-brand="true" style={{ textAlign: "right" }}>
                        <img
                          src="https://d2xsxph8kpxj0f.cloudfront.net/310419663029793542/5GqF8Kn9QcwXve8h8refaY/apx-logo-final_808b0f18.png"
                          alt="APX"
                          style={{ height: "52px", width: "52px", objectFit: "contain" }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Report table */}
                  <ReportTable report={activeReport} />
                </>
              ) : (
                <div style={s.emptyState}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="oklch(0.14 0.04 240)" strokeWidth="1.2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: 600, color: "oklch(0.14 0.04 240)", marginBottom: "4px" }}>
                      No report loaded
                    </div>
                    <div style={{ fontSize: "13px" }}>
                      Select a date range and click <strong style={{ color: "oklch(0.08 0.01 240)" }}>Run Report</strong> to fetch live data from Xero.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Print styles — compact single-page layout */}
      <style>{`
        @page {
          size: Letter portrait;
          margin: 0.4in 0.45in;
        }

        @media print {
          /* ── Step 1: hide ALL content via visibility (not display, so layout is preserved) ── */
          body * {
            visibility: hidden !important;
          }

          /* ── Step 2: show only the print-area and everything inside it ── */
          .print-area,
          .print-area * {
            visibility: visible !important;
          }

          /* ── Step 3: position print-area at top-left of the page ── */
          .print-area {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: #fff !important;
            color: #111 !important;
            overflow: visible !important;
            z-index: 9999 !important;
          }

          body, html {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          /* ── Light theme override for all print-area children ── */
          .print-area *:not([data-neg]) {
            background: transparent !important;
            color: #111 !important;
            border-color: #ddd !important;
            box-shadow: none !important;
          }

          /* ── Report header: compact ── */
          .print-area > div:first-child {
            padding: 8px 0 6px !important;
            border-bottom: 1.5px solid #333 !important;
            margin-bottom: 4px !important;
          }

          /* ── Table: full width, tight ── */
          .print-area table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 8.5pt !important;
            font-family: Arial, Helvetica, sans-serif !important;
          }

          /* ── All cells: minimal padding ── */
          .print-area th,
          .print-area td {
            padding: 2.5px 6px !important;
            font-size: 8.5pt !important;
            line-height: 1.25 !important;
            border: none !important;
          }

          /* ── Section header rows ── */
          .print-area tr td[colspan] {
            padding-top: 6px !important;
            padding-bottom: 2px !important;
            font-weight: 700 !important;
            font-size: 8.5pt !important;
            border-top: 1px solid #bbb !important;
            background: #f5f5f5 !important;
          }

          /* ── Column header row ── */
          .print-area th {
            border-bottom: 1.5px solid #333 !important;
            font-weight: 700 !important;
            color: #333 !important;
          }

          /* ── Summary / total rows ── */
          .print-area tr[data-summary] td {
            padding: 3px 6px !important;
            font-weight: 700 !important;
            border-top: 1.5px solid #555 !important;
          }

          /* ── Negative values: red ── */
          .print-area [data-neg="true"] {
            color: #c0392b !important;
          }

          /* ── APX branding: dark gold ── */
          .print-area [data-brand] {
            color: #8B6914 !important;
            font-weight: 700 !important;
          }

          /* ── Page break control ── */
          .print-area tr { page-break-inside: avoid; }
        }
      `}</style>

      {/* ─── Reconnect safeguard confirmation dialog ─────────────────────────── */}
      {reconnectConfirm && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "oklch(0.05 0.02 240 / 0.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          backdropFilter: "blur(4px)",
        }}>
          <div style={{
            background: "oklch(0.94 0.018 220)",
            border: "1px solid oklch(0.88 0.01 240)",
            borderRadius: "16px",
            padding: "2rem",
            maxWidth: 420,
            width: "90%",
            boxShadow: "0 24px 64px oklch(0 0 0 / 0.6)",
          }}>
            {/* Icon + title */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "1.25rem" }}>
              <div style={{
                width: 40, height: 40, borderRadius: "10px",
                background: "oklch(0.92 0.005 240 / 0.15)",
                border: "1px solid oklch(0.92 0.005 240 / 0.3)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="oklch(0.82 0.01 240)" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "oklch(0.18 0.02 240)" }}>
                  Reconnect {reconnectConfirm.name}
                </h3>
                <p style={{ margin: 0, fontSize: "13px", color: "oklch(0.14 0.04 240)" }}>
                  Verify your Xero account before continuing
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div style={{
              background: "oklch(0.94 0.018 220)",
              border: "1px solid oklch(0.97 0.005 85)",
              borderRadius: "10px",
              padding: "1rem 1.25rem",
              marginBottom: "1.5rem",
            }}>
              <p style={{ margin: "0 0 0.75rem", fontSize: "13px", color: "oklch(0.14 0.04 240)", lineHeight: 1.5 }}>
                Before clicking <strong style={{ color: "oklch(0.18 0.02 240)" }}>Continue</strong>, make sure you are signed into Xero as:
              </p>
              <div style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 12px",
                background: "oklch(0.92 0.005 240 / 0.1)",
                border: "1px solid oklch(0.92 0.005 240 / 0.3)",
                borderRadius: "8px",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="oklch(0.82 0.01 240)" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "oklch(0.08 0.01 240)", fontFamily: "monospace" }}>
                  {reconnectConfirm.email}
                </span>
              </div>
              <p style={{ margin: "0.75rem 0 0", fontSize: "13px", color: "oklch(0.28 0.03 240)", lineHeight: 1.5 }}>
                Connecting the wrong account will overwrite the existing token for <strong>{reconnectConfirm.name}</strong> and break its reports.
              </p>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button
                onClick={() => setReconnectConfirm(null)}
                style={{
                  flex: 1, padding: "10px", fontSize: "13px", fontWeight: 500,
                  background: "transparent",
                  color: "oklch(0.14 0.04 240)",
                  border: "1px solid oklch(0.88 0.01 240)",
                  borderRadius: "8px", cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReconnectConfirmed}
                style={{
                  flex: 2, padding: "10px", fontSize: "13px", fontWeight: 600,
                  background: "oklch(0.82 0.01 240)",
                  color: "oklch(0.12 0.04 240)",
                  border: "none",
                  borderRadius: "8px", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                I'm signed in as {reconnectConfirm.email.split("@")[0]} — Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Controller Approval Dialog for Automation ───────────────────────────────────────────── */}
      {showApprovalDialog && pendingJobInfo && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10000,
          background: "oklch(0.05 0.02 240 / 0.88)",
          display: "flex", alignItems: "center", justifyContent: "center",
          backdropFilter: "blur(6px)",
        }}>
          <div style={{
            background: "oklch(0.94 0.018 220)",
            border: "1.5px solid oklch(0.92 0.005 240 / 0.4)",
            borderRadius: "18px",
            padding: "2rem",
            maxWidth: 460,
            width: "92%",
            boxShadow: "0 32px 80px oklch(0 0 0 / 0.7)",
          }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "1.5rem" }}>
              <div style={{
                width: 44, height: 44, borderRadius: "12px",
                background: "oklch(0.92 0.005 240 / 0.15)",
                border: "1.5px solid oklch(0.92 0.005 240 / 0.4)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="oklch(0.82 0.01 240)" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "oklch(0.18 0.02 240)" }}>
                  Controller Approval Required
                </h3>
                <p style={{ margin: 0, fontSize: "13px", color: "oklch(0.14 0.04 240)" }}>
                  Xero login authorization for report generation
                </p>
              </div>
            </div>

            {/* Job details */}
            <div style={{
              background: "oklch(0.96 0.008 240)",
              border: "1px solid oklch(0.88 0.01 240)",
              borderRadius: "12px",
              padding: "1rem 1.25rem",
              marginBottom: "1.25rem",
            }}>
              <div style={{ fontSize: "13px", color: "oklch(0.14 0.04 240)", marginBottom: "0.75rem" }}>
                The system will log into Xero to generate reports for:
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                  <span style={{ color: "oklch(0.14 0.04 240)" }}>Client</span>
                  <span style={{ color: "oklch(0.18 0.02 240)", fontWeight: 600 }}>{pendingJobInfo.clientName}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                  <span style={{ color: "oklch(0.14 0.04 240)" }}>Practice</span>
                  <span style={{ color: "oklch(0.08 0.01 240)", fontWeight: 600 }}>{pendingJobInfo.practiceKey.toUpperCase()}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                  <span style={{ color: "oklch(0.14 0.04 240)" }}>Xero Login</span>
                  <span style={{ color: "oklch(0.08 0.01 240)", fontFamily: "monospace", fontSize: "13px" }}>{pendingJobInfo.practiceEmail}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                  <span style={{ color: "oklch(0.14 0.04 240)" }}>Date Range</span>
                  <span style={{ color: "oklch(0.14 0.04 240)" }}>{pendingJobInfo.fromDate} → {pendingJobInfo.toDate}</span>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div style={{
              background: "oklch(0.92 0.005 240 / 0.08)",
              border: "1px solid oklch(0.92 0.005 240 / 0.25)",
              borderRadius: "10px",
              padding: "0.875rem 1rem",
              marginBottom: "1.5rem",
              fontSize: "13px",
              color: "oklch(0.08 0.01 240)",
              lineHeight: 1.6,
            }}>
              <strong style={{ color: "oklch(0.08 0.01 240)" }}>Important:</strong> By approving, you confirm that you are aware Manus will log into Xero as <strong>{pendingJobInfo.practiceEmail}</strong> to generate these reports. This action is logged.
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button
                onClick={() => { setShowApprovalDialog(false); setPendingJobInfo(null); }}
                style={{
                  flex: 1, padding: "11px", fontSize: "13px", fontWeight: 500,
                  background: "transparent",
                  color: "oklch(0.14 0.04 240)",
                  border: "1px solid oklch(0.88 0.01 240)",
                  borderRadius: "9px", cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!pendingJobInfo) return;
                  approveJobMut.mutate({
                    jobId: pendingJobInfo.jobId,
                    fromDate: pendingJobInfo.fromDate,
                    toDate: pendingJobInfo.toDate,
                  });
                }}
                disabled={approveJobMut.isPending}
                style={{
                  flex: 2, padding: "11px", fontSize: "13px", fontWeight: 700,
                  background: approveJobMut.isPending ? "oklch(0.55 0.01 240)" : "oklch(0.82 0.01 240)",
                  color: "oklch(0.12 0.04 240)",
                  border: "none",
                  borderRadius: "9px",
                  cursor: approveJobMut.isPending ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                }}
              >
                {approveJobMut.isPending ? (
                  <>
                    <div className="animate-spin" style={{ width: 14, height: 14, border: "2px solid oklch(0.12 0.04 240 / 30%)", borderTopColor: "oklch(0.97 0.005 85)", borderRadius: "50%" }} />
                    Starting...
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    Approve &amp; Generate Reports
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── Session Cookie Panel ───────────────────────────────────────────────────────────── */}
      {showCookiePanel && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "oklch(0 0 0 / 0.65)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "1rem",
        }}>
          <div style={{
            background: "oklch(0.94 0.018 220)",
            border: "1.5px solid oklch(0.92 0.005 240 / 0.4)",
            borderRadius: "16px",
            padding: "1.5rem",
            width: "100%",
            maxWidth: 560,
            boxShadow: "0 24px 64px oklch(0 0 0 / 0.6)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "oklch(0.18 0.02 240)" }}>Paste Xero Session Cookies</div>
                <div style={{ fontSize: "13px", color: "oklch(0.14 0.04 240)", marginTop: "3px" }}>
                  Practice: <strong style={{ color: "oklch(0.08 0.01 240)" }}>{cookiePanelPractice?.toUpperCase()}</strong>
                </div>
              </div>
              <button
                onClick={() => { setShowCookiePanel(false); setCookieInputValue(""); }}
                style={{ background: "none", border: "none", color: "oklch(0.14 0.04 240)", cursor: "pointer", fontSize: "20px", lineHeight: 1, padding: "4px" }}
              >✕</button>
            </div>
            <div style={{ fontSize: "13px", color: "oklch(0.28 0.04 240)", marginBottom: "0.75rem", lineHeight: 1.7, background: "oklch(0.1 0.02 240)", borderRadius: "8px", padding: "0.75rem" }}>
              <strong style={{ color: "oklch(0.12 0.04 240)" }}>How to get your session cookie:</strong><br />
              1. Log into Xero in Chrome as the <strong style={{ color: "oklch(0.08 0.01 240)" }}>{cookiePanelPractice?.toUpperCase()}</strong> practice user<br />
              2. Press <strong>F12</strong> → click the <strong>Network</strong> tab → press <strong>F5</strong> to refresh<br />
              3. Click any request to <strong>go.xero.com</strong> → <strong>Headers</strong> → scroll to <strong>Request Headers</strong><br />
              4. Find the line starting with <strong>cookie:</strong> — copy the entire value and paste below
            </div>
            <textarea
              value={cookieInputValue}
              onChange={e => setCookieInputValue(e.target.value)}
              placeholder="Paste the full cookie string here (e.g. .ASPXAUTH=abc123; __RequestVerificationToken=xyz...)"
              rows={6}
              style={{
                width: "100%",
                background: "oklch(0.1 0.02 240)",
                border: "1px solid oklch(0.3 0.04 240)",
                borderRadius: "8px",
                color: "oklch(0.14 0.04 240)",
                fontSize: "13px",
                fontFamily: "monospace, sans-serif",
                padding: "0.75rem",
                resize: "vertical",
                outline: "none",
                boxSizing: "border-box" as const,
              }}
            />
            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem", justifyContent: "flex-end" }}>
              <button
                onClick={() => { setShowCookiePanel(false); setCookieInputValue(""); }}
                style={{ padding: "8px 18px", borderRadius: "8px", border: "1px solid oklch(0.3 0.04 240)", background: "transparent", color: "oklch(0.14 0.04 240)", cursor: "pointer", fontSize: "13px" }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!cookiePanelPractice || !cookieInputValue.trim()) return;
                  await saveSessionCookiesMut.mutateAsync({
                    practiceKey: cookiePanelPractice,
                    cookieString: cookieInputValue.trim(),
                  });
                  setShowCookiePanel(false);
                  setCookieInputValue("");
                }}
                disabled={saveSessionCookiesMut.isPending || !cookieInputValue.trim()}
                style={{
                  padding: "8px 22px",
                  borderRadius: "8px",
                  border: "none",
                  background: saveSessionCookiesMut.isPending || !cookieInputValue.trim() ? "oklch(0.35 0.01 240)" : "oklch(0.82 0.01 240)",
                  color: "oklch(0.1 0.02 240)",
                  fontWeight: 700,
                  fontSize: "13px",
                  cursor: saveSessionCookiesMut.isPending || !cookieInputValue.trim() ? "not-allowed" : "pointer",
                }}
              >
                {saveSessionCookiesMut.isPending ? "Saving..." : "Save Cookies"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── Automation Running Banner ────────────────────────────────────────────────────── */}
      {automationJobId && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9998,
          background: "oklch(0.94 0.018 220)",
          border: "1.5px solid oklch(0.92 0.005 240 / 0.4)",
          borderRadius: "14px",
          padding: "1rem 1.25rem",
          maxWidth: 340,
          boxShadow: "0 16px 48px oklch(0 0 0 / 0.5)",
          display: "flex", alignItems: "center", gap: "12px",
        }}>
          <div className="animate-spin" style={{ width: 20, height: 20, border: "2px solid oklch(0.92 0.005 240 / 30%)", borderTopColor: "oklch(0.82 0.01 240)", borderRadius: "50%", flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "oklch(0.18 0.02 240)" }}>Generating Reports...</div>
            <div style={{ fontSize: "13px", color: "oklch(0.14 0.04 240)", marginTop: "2px" }}>
              {(jobStatusQuery.data as { message?: string } | undefined)?.message ?? "Logging into Xero..."}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
